// src/encounter/device-trends/device-trends-card.tsx
// DeviceTrendsCard (level: card) — "Patient device data", the patient's OWN wearable trends, read-only, for the physician.
// Ported directly from Claude Design "Patient device data card", round 3 (1 Nov 2026), drawn on Antonin's design system
// "Scrips Apps — Practice + Practitioner" (Samer: "follow Antonin rules, period"). Its look lives in the showcase area sheet
// src/styles/showcase/encounter-device-data.css (the PA's only styling path); this file is markup and behaviour.
//   · variant 'section': one section of the started encounter's chart column, after Patient flags, with the neighbours'
//     own title + count (the host's chart-section rule draws the box).
//   · variant 'card': its own card in the pre-visit main column, directly under the vitals.
// Compact by default: one row per series; a row opens its 30-day chart on click or Enter/Space only (a native button);
// nothing opens or re-flows on hover or focus. "Show trends" / "Hide trends" opens or closes every row.
// Clinical sign-off (1 Nov): no meaning colour, range, target, arrow, flag or AI text on device values; one neutral series
// colour; daily readings as dots joined by a faint hairline; a day with no reading is a gap; an isolated reading keeps its
// dot; the most recent a larger dot; the y axis spans at least 20 beats/min, 20 ms or 4 h, centred, never below zero.
// Hidden (no consent / no device) is the host's call: DeviceTrendsSlot renders nothing.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { Button, Icon } from '@scripsteam/scrips-signal-ds';
import { V2_META_STRONG } from '@/shared/ui/design-v2';
import { WEARABLE_EMPTY_LINE, WEARABLE_SOURCE_LINE, type WearableSeries } from '../lib/wearable-trends';
import { buildOption, formatDay, formatSync, formatValue } from './device-trends-format';
import { railDate } from '../lib/rail-date';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

echarts.use([LineChart, GridComponent, SVGRenderer]);

export type DeviceTrendsState = 'loading' | 'error' | 'empty' | 'ready';

export interface DeviceTrendsCardProps {
  state: DeviceTrendsState;
  series?: WearableSeries[];
  /** Device names, shown in the empty state. */
  devices?: string[];
  onRetry?: () => void;
  /** Keys of open rows. Pass to control; omit for uncontrolled (collapsed by default). */
  expanded?: string[];
  /** Initial open keys when uncontrolled. */
  defaultExpanded?: string[];
  onExpandedChange?: (keys: string[]) => void;
  variant?: 'card' | 'section';
}

function cssVar(el: Element, name: string) {
  return getComputedStyle(el).getPropertyValue(name).trim();
}

function TrendChart({ series }: { series: WearableSeries }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const chart = echarts.init(el, undefined, { renderer: 'svg' });
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let timer: number | undefined;
    const draw = () => {
      // The showcase sheet's tokens, each falling back to the DS token it is built on, so a host without the showcase
      // layer (Storybook) still draws in the same neutral colours.
      const tokens = {
        dot: cssVar(el, '--device-trend-dot') || cssVar(el, '--color-grey-700'),
        line: cssVar(el, '--device-trend-line') || cssVar(el, '--color-grey-400'),
        grid: cssVar(el, '--surface-line') || cssVar(el, '--color-border-card'),
        meta: cssVar(el, '--ink-meta') || cssVar(el, '--color-text-muted'),
        font: cssVar(el, '--font-sans'),
      };
      // No stylesheet applied yet at all: retry rather than paint with empty colours.
      if (!tokens.dot) {
        timer = window.setTimeout(draw, 100);
        return;
      }
      chart.setOption(buildOption(series, tokens, reduceMotion), true);
    };
    draw();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => chart.resize());
    ro?.observe(el);
    return () => {
      window.clearTimeout(timer);
      ro?.disconnect();
      chart.dispose();
    };
  }, [series]);
  return (
    <>
      <div ref={ref} data-slot="device-trend-chart" role="img" aria-label={`${series.label}, last 30 days, ${series.device}`} />
      {/* The table alternative to the chart (clinical sign-off, item 6): every day with a reading. */}
      <table data-slot="device-trend-table" className="sr-only">
        <caption>{`${series.label}, ${series.device}, readings in the last 30 days`}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">{`Value (${series.unit})`}</th>
          </tr>
        </thead>
        <tbody>
          {series.points
            .filter((p) => p.value !== null)
            .map((p) => (
              <tr key={p.date}>
                <td>{formatDay(p.date, true)}</td>
                <td>{formatValue(series.unit, p.value as number)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </>
  );
}

/** `datesOnly` (`?x=rail-fixes`, in the left rail; Andrew, 4 Nov: "remove the time from all the dates"): the sync day without its time. */
function footer(sr: WearableSeries, datesOnly = false) {
  const sync = sr.lastSyncAt ? (datesOnly ? railDate(sr.lastSyncAt) : formatSync(sr.lastSyncAt)) : '';
  return [sr.loinc ? `LOINC ${sr.loinc}` : null, sync ? `Last sync ${sync}` : null].filter(Boolean).join(' · ');
}

function Row({ sr, open, onToggle, panelId, datesOnly }: { sr: WearableSeries; open: boolean; onToggle: () => void; panelId: string; datesOnly?: boolean }) {
  const foot = footer(sr, datesOnly);
  return (
    <li data-slot="device-trend-row" data-series={sr.key}>
      <button type="button" data-slot="device-trend-toggle" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
        <span data-slot="device-trend-label">{sr.label}</span>
        <span data-slot="device-trend-device">
          {sr.device}
          {!sr.connected ? <span data-slot="device-trend-disconnected"> · Disconnected</span> : null}
        </span>
        <span data-slot="device-trend-value">
          {formatValue(sr.unit, sr.latest.value)}
          <span data-slot="device-trend-unit"> {sr.unit}</span>
        </span>
        {/* D-45 with the year (clinical sign-off m1). */}
        <time data-slot="device-trend-date" dateTime={sr.latest.date}>
          {formatDay(sr.latest.date, true)}
        </time>
        <span data-slot="device-trend-chev" aria-hidden="true">
          <Icon name="down" size={16} />
        </span>
      </button>
      {open ? (
        <div id={panelId} data-slot="device-trend-panel">
          <TrendChart series={sr} />
          {foot ? <p data-slot="device-trend-footer">{foot}</p> : null}
        </div>
      ) : null}
    </li>
  );
}

export function DeviceTrendsCard({
  state,
  series = [],
  devices = [],
  onRetry,
  expanded,
  defaultExpanded = [],
  onExpandedChange,
  variant = 'card',
}: DeviceTrendsCardProps) {
  const uid = useId();
  const [own, setOwn] = useState<string[]>(defaultExpanded);
  const open = expanded ?? own;
  const setOpen = useCallback(
    (keys: string[]) => {
      if (expanded === undefined) setOwn(keys);
      onExpandedChange?.(keys);
    },
    [expanded, onExpandedChange],
  );
  const ready = state === 'ready' && series.length > 0;
  const allOpen = ready && series.every((sr) => open.includes(sr.key));
  const toggle = (key: string) => setOpen(open.includes(key) ? open.filter((k) => k !== key) : [...open, key]);
  const isSection = variant === 'section';
  // The left rail draws dates without a time (`?x=rail-fixes`); the pre-visit's own card ('card') keeps its sync time. Read once per mount.
  const [railFixes] = useState(() => isEncounterFlagOn('railFixes'));
  const datesOnly = isSection && railFixes;

  return (
    <section
      data-device-trends=""
      data-variant={variant}
      data-state={state}
      // In the chart column, one more section: the host's chart-section rule draws its box, like its neighbours.
      data-slot={isSection ? 'chart-section' : 'device-trends-card'}
      data-section={isSection ? 'device-data' : undefined}
      aria-label="Patient device data"
      aria-busy={state === 'loading' || undefined}
    >
      <div data-slot="device-trends-head">
        {isSection ? (
          <div className="flex min-w-0 items-center gap-1">
            <span data-slot="chart-section-title" className={V2_META_STRONG}>
              Patient device data
            </span>
            {ready ? (
              <span data-slot="chart-section-count" className={V2_META_STRONG}>
                {series.length}
              </span>
            ) : null}
          </div>
        ) : (
          <h3 data-slot="device-trends-title">
            Patient device data
            {ready ? <span data-slot="device-trends-count">{series.length}</span> : null}
          </h3>
        )}
        {ready ? (
          <Button variant="tertiary" size="sm" aria-expanded={allOpen} onClick={() => setOpen(allOpen ? [] : series.map((sr) => sr.key))}>
            {allOpen ? 'Hide trends' : 'Show trends'}
          </Button>
        ) : null}
      </div>
      <p data-slot="device-trends-source">{WEARABLE_SOURCE_LINE}</p>
      {state === 'loading' ? (
        <ul data-slot="device-trends-list" aria-hidden="true">
          {[0, 1].map((i) => (
            <li key={i} data-slot="device-trends-skeleton">
              <span data-slot="device-trends-bar" data-bar="label" />
              <span data-slot="device-trends-bar" data-bar="value" />
              <span data-slot="device-trends-bar" data-bar="meta" />
            </li>
          ))}
        </ul>
      ) : state === 'error' ? (
        <div data-slot="device-trends-notice" role="alert">
          <p data-slot="device-trends-notice-text">Device data could not be loaded.</p>
          {onRetry ? (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          ) : null}
        </div>
      ) : !ready ? (
        <div data-slot="device-trends-empty">
          <p data-slot="device-trends-notice-text">{WEARABLE_EMPTY_LINE}</p>
          {devices.length ? <p data-slot="device-trends-empty-device">{devices.join(', ')}</p> : null}
        </div>
      ) : (
        <ul data-slot="device-trends-list">
          {series.map((sr) => (
            <Row key={sr.key} sr={sr} open={open.includes(sr.key)} onToggle={() => toggle(sr.key)} panelId={`${uid}-${sr.key}`} datesOnly={datesOnly} />
          ))}
        </ul>
      )}
    </section>
  );
}
